import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Translations ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Mes signalements',
    sousTitre: 'Historique de vos signalements de braconnage et comportements anormaux.',
    retour: '← Tableau de bord',
    nouveau: '+ Nouveau signalement',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger vos signalements.',
    aucun: "Vous n'avez aucun signalement pour l'instant.",
    soumettre: 'Signaler un behavior suspect',
    types: {
      braconnage: 'Braconnage',
      tir_hors_zone: 'Tir hors zone',
      intrusion: 'Intrusion',
      comportement_anormal: 'Comportement anormal',
      autre: 'Autre',
    },
    statuts: {
      recu: 'Reçu',
      en_cours: 'En cours',
      resolu: 'Résolu',
      ferme: 'Fermé',
      transmis: 'Transmis',
    },
    statutColors: {
      recu: '#d97706',
      en_cours: '#2563eb',
      resolu: '#16a34a',
      ferme: '#6b7280',
      transmis: '#7c3aed',
    },
    date: 'Date',
    localisation: 'Localisation',
    transmis: 'Transmis à un organisme',
    oui: 'Oui',
    non: 'Non',
    precedent: '← Précédent',
    suivant: 'Suivant →',
    page: (p, t) => `Page ${p} / ${t}`,
  },
  en: {
    title: 'My Reports',
    sousTitre: 'History of your poaching and suspicious behaviour reports.',
    retour: '← Dashboard',
    nouveau: '+ New report',
    chargement: 'Loading…',
    erreur: 'Failed to load your reports.',
    aucun: 'You have no reports yet.',
    soumettre: 'Report suspicious behaviour',
    types: {
      braconnage: 'Poaching',
      tir_hors_zone: 'Out-of-zone shooting',
      intrusion: 'Trespassing',
      comportement_anormal: 'Abnormal behaviour',
      autre: 'Other',
    },
    statuts: {
      recu: 'Received',
      en_cours: 'In progress',
      resolu: 'Resolved',
      ferme: 'Closed',
      transmis: 'Forwarded',
    },
    statutColors: {
      recu: '#d97706',
      en_cours: '#2563eb',
      resolu: '#16a34a',
      ferme: '#6b7280',
      transmis: '#7c3aed',
    },
    date: 'Date',
    localisation: 'Location',
    transmis: 'Forwarded to authority',
    oui: 'Yes',
    non: 'No',
    precedent: '← Previous',
    suivant: 'Next →',
    page: (p, t) => `Page ${p} / ${t}`,
  },
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function MesSignalements() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [reports, setSignalements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const cancelRef = useRef(null);

  const load = useCallback(
    async (p = 1) => {
      if (cancelRef.current) cancelRef.current = false;
      const token = {};
      cancelRef.current = token;

      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/reports/my-reports?page=${p}&limit=10`);
        if (cancelRef.current !== token) return;
        setSignalements(data.data || []);
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
        setPage(p);
      } catch {
        if (cancelRef.current !== token) return;
        setError(t.erreur);
      } finally {
        if (cancelRef.current === token) setLoading(false);
      }
    },
    [t.erreur]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="🚨" title={t.title} subtitle={t.sousTitre} />

        {/* New report CTA */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Link
            to="/submit-report"
            style={{
              display: 'inline-block',
              padding: '.55rem 1.2rem',
              background: 'var(--couleur-or)',
              color: '#000',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '.9rem',
            }}
          >
            {t.nouveau}
          </Link>
        </div>

        {/* States */}
        {loading && <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.chargement}</p>}
        {error && <p style={{ color: 'var(--couleur-texte-erreur)' }}>{error}</p>}

        {!loading && !error && reports.length === 0 && (
          <div
            style={{
              background: 'var(--couleur-fond)',
              borderRadius: 'var(--rayon)',
              padding: '2.5rem',
              textAlign: 'center',
            }}
          >
            <p style={{ color: 'var(--couleur-texte-attenué)', marginBottom: '1rem' }}>{t.aucun}</p>
            <Link
              to="/submit-report"
              style={{ color: 'var(--couleur-or)', textDecoration: 'none', fontWeight: 600 }}
            >
              {t.soumettre} →
            </Link>
          </div>
        )}

        {/* List */}
        {!loading && reports.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {reports.map((s) => {
              const couleurStatut = t.statutColors[s.status] || '#888';
              return (
                <div
                  key={s.id}
                  style={{
                    background: 'var(--couleur-fond)',
                    borderRadius: 'var(--rayon)',
                    padding: '1.25rem',
                    borderLeft: `4px solid ${couleurStatut}`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '.5rem',
                      marginBottom: '.75rem',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                        {t.types[s.report_type] || s.report_type}
                      </span>
                      <span
                        style={{
                          marginLeft: '.75rem',
                          fontSize: '.78rem',
                          fontWeight: 600,
                          padding: '.2rem .6rem',
                          borderRadius: 12,
                          background: couleurStatut + '22',
                          color: couleurStatut,
                          border: `1px solid ${couleurStatut}44`,
                        }}
                      >
                        {t.statuts[s.status] || s.status}
                      </span>
                    </div>
                    <span style={{ color: 'var(--couleur-texte-attenué)', fontSize: '.82rem' }}>
                      {new Date(s.createdAt).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  <p style={{ margin: '0 0 .5rem', fontSize: '.9rem', lineHeight: 1.5 }}>
                    {s.description}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      gap: '1.5rem',
                      flexWrap: 'wrap',
                      fontSize: '.8rem',
                      color: 'var(--couleur-texte-attenué)',
                    }}
                  >
                    {s.lat && s.lng && (
                      <span>
                        {t.localisation}: {parseFloat(s.lat).toFixed(4)},{' '}
                        {parseFloat(s.lng).toFixed(4)}
                      </span>
                    )}
                    {s.observed_at && (
                      <span>
                        {t.date}: {new Date(s.observed_at).toLocaleDateString()}
                      </span>
                    )}
                    <span>
                      {t.transmis}:{' '}
                      <span
                        style={{
                          color: s.transmitted_to_org ? '#4ade80' : 'var(--couleur-texte-attenué)',
                        }}
                      >
                        {s.transmitted_to_org ? t.oui : t.non}
                      </span>
                    </span>
                  </div>

                  {Array.isArray(s.photos) && s.photos.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '.5rem',
                        marginTop: '.75rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {s.photos.slice(0, 4).map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt=""
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #333',
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginTop: '1.5rem',
            }}
          >
            <button
              onClick={() => load(page - 1)}
              disabled={page <= 1}
              style={{
                padding: '.45rem 1rem',
                background: 'transparent',
                color: page > 1 ? 'var(--couleur-or)' : '#444',
                border: '1px solid #444',
                borderRadius: 6,
                cursor: page > 1 ? 'pointer' : 'default',
              }}
            >
              {t.precedent}
            </button>
            <span style={{ color: 'var(--couleur-texte-attenué)', fontSize: '.88rem' }}>
              {t.page(page, totalPages)}
            </span>
            <button
              onClick={() => load(page + 1)}
              disabled={page >= totalPages}
              style={{
                padding: '.45rem 1rem',
                background: 'transparent',
                color: page < totalPages ? 'var(--couleur-or)' : '#444',
                border: '1px solid #444',
                borderRadius: 6,
                cursor: page < totalPages ? 'pointer' : 'default',
              }}
            >
              {t.suivant}
            </button>
          </div>
        )}
      </main>
    </PrivateLayout>
  );
}
